import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { Restaurant } from '../../types';

export interface RestaurantImage {
  id: number;
  url: string;
  caption?: string;
}

export interface MenuItem {
  id: number;
  name: string;
  description?: string;
  price: number;
  category?: string;
  image_url?: string;
}

export interface Review {
  id: number;
  user: {
    id: number;
    username: string;
    avatar_url?: string;
  };
  rating: number;
  comment?: string;
  created_at: string;
}

interface RestaurantState {
  items: Restaurant[];
  selectedRestaurant: Restaurant | null;
  loading: boolean;
  error: string | null;
}

const initialState: RestaurantState = {
  items: [],
  selectedRestaurant: null,
  loading: false,
  error: null,
};

export const fetchRestaurants = createAsyncThunk<
  Restaurant[],
  void,
  { rejectValue: string }
>(
  'restaurants/fetchRestaurants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<{ results: Restaurant[] }>('/api/restaurants/');
      return response.data.results;
    } catch (error) {
      return rejectWithValue('Failed to fetch restaurants');
    }
  }
);

export const fetchRestaurantById = createAsyncThunk<
  Restaurant,
  number,
  { rejectValue: string }
>(
  'restaurants/fetchRestaurantById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/restaurants/${id}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch restaurant details');
    }
  }
);

const restaurantSlice = createSlice({
  name: 'restaurants',
  initialState,
  reducers: {
    setSelectedRestaurant: (state, action: PayloadAction<Restaurant | null>) => {
      state.selectedRestaurant = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRestaurants.pending, (state: RestaurantState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRestaurants.fulfilled, (state: RestaurantState, action: PayloadAction<Restaurant[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchRestaurants.rejected, (state: RestaurantState, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchRestaurantById.pending, (state: RestaurantState) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRestaurantById.fulfilled, (state: RestaurantState, action) => {
        state.loading = false;
        state.selectedRestaurant = action.payload;
      })
      .addCase(fetchRestaurantById.rejected, (state: RestaurantState, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedRestaurant } = restaurantSlice.actions;
export default restaurantSlice.reducer; 